/**
 * V3 Quiz Funnel — Flow Router
 *
 * Step sequence (24 screens):
 *  1. motivation        — Motivation preference (single-select)
 *  2. motivation_intro  — Trust screen: 50,000 members + live 4–5★ reviews carousel
 *  3. age              — Age selection (30-39 / 40-49 / 50-59 / 60+)
 *  4. activity          — Activity level (single-select)
 *  5. interstitial_metabolism — Dark interstitial (auto-advance 6s)
 *  6. stress_symptoms   — Stress symptom picker (multi-select)
 *  7. sleep             — Sleep quality (single-select)
 *  8. physical_barriers — Physical barriers (multi-select)
 *  9. interstitial_science — Zone 2 science interstitial (auto-advance 6s)
 * 10. walking_history   — Walking experience (single-select)
 * 11. weight_pattern    — Weight pattern (single-select)
 * 12. interstitial_reframe — Cortisol loop interstitial (auto-advance 6s)
 * 13. primary_goal      — Primary goal (single-select)
 * 14. main_barrier      — Main barrier (single-select)
 * 15. reward_preference — Reward brand (single-select)
 * 16. email_capture     — Name + email form
 * 17. plan_loading      — Animated plan-building loader (5s)
 * 18. results           — 6-section personalized conversion page
 * 19. paywall           — Plan selection
 * 20. payment           — Stripe checkout (card + Apple/Google Pay)
 * 21. create_account    — Password creation + registerUser
 * 22. coaching_upsell   — Coaching add-on (soft skip)
 * 23. exclusive_offer   — Exclusive sign-up offer (one-time purchase, soft skip)
 * 24. get_app           — App Store / Play Store download
 */

import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, Suspense } from 'react';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { useFunnelV3Store, FunnelV3Step, PlanType } from '../../store/funnelV3Store';
import { getExpressCheckoutAmountCents } from './steps/paymentPricing';
import FunnelV3ProgressBar from './FunnelV3ProgressBar';
import { FUNNEL_V3_STEP_ORDER, getQuestionSteps, shouldSkipOnBack } from './funnelV3StepConfig';
import { trackStepViewed, trackQuizCompleted, trackPageView } from './analytics/funnelV3Analytics';
import { ensureAbDefaultPlanVariant } from '../../lib/abDefaultPlan';
import { ensureDynamicPricingVariant } from '../../lib/dynamicPricingAb';
import {
    buildPricingTierSignalsFromFunnelData,
    ensurePricingTier,
    getPricingBand,
} from '../../lib/pricingTier';
import { trackPricingTierAssigned } from './analytics/funnelV3Analytics';
import { preloadStripe, StripeProvider } from './StripeProvider';
import { getSessionStorageItem, setSessionStorageItem } from '../../utils/safeSessionStorage';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import { hydrateQuizFromProfile } from '../../utils/hydrateQuizFromProfile';
import { ensureFunnelAccessToken } from '../../utils/funnelAuth';
import { destinationAfterSession } from '../../utils/resumePaidCheckout';
import { captureReferralCodeFromUrl } from '../../utils/utm';
import { validateReferralCode } from '../../services/api';
import {
    trackReferralBannerCodeInvalid,
    trackReferralBannerShown,
} from './analytics/funnelV3Analytics';
import { useAuth } from '../../context/AuthContext';
import { ErrorBoundary, PaymentErrorFallback } from '../ErrorBoundary';
import * as Sentry from '@sentry/react';

// Only the landing step is in the initial bundle. The remaining quiz steps
// load on demand; MotivationStep prefetches its immediate successor after the
// first answer while its 1.7s affirmation is visible.
import MotivationStep from './steps/MotivationStep';
const MotivationInterstitial = lazyWithRetry(() => import('./steps/MotivationInterstitial'), 'motivation_intro');
const GoalsIntroStep = lazyWithRetry(() => import('./steps/GoalsIntroStep'), 'goals_intro');
const AgeStep = lazyWithRetry(() => import('./steps/AgeStep'), 'age');
const ActivityStep = lazyWithRetry(() => import('./steps/ActivityStep'), 'activity');
const InterstitialMetabolism = lazyWithRetry(() => import('./steps/InterstitialMetabolism'), 'interstitial_metabolism');
const StressSymptomsStep = lazyWithRetry(() => import('./steps/StressSymptomsStep'), 'stress_symptoms');
const SleepStep = lazyWithRetry(() => import('./steps/SleepStep'), 'sleep');
const InterstitialSleep = lazyWithRetry(() => import('./steps/InterstitialSleep'), 'interstitial_sleep');
const PhysicalBarriersStep = lazyWithRetry(() => import('./steps/PhysicalBarriersStep'), 'physical_barriers');
const TargetZonesStep = lazyWithRetry(() => import('./steps/TargetZonesStep'), 'target_zones');
const InterstitialScience = lazyWithRetry(() => import('./steps/InterstitialScience'), 'interstitial_science');
const HeightStep = lazyWithRetry(() => import('./steps/HeightStep'), 'height');
const CurrentWeightStep = lazyWithRetry(() => import('./steps/CurrentWeightStep'), 'current_weight');
const GoalWeightStep = lazyWithRetry(() => import('./steps/GoalWeightStep'), 'goal_weight');
const BirthYearStep = lazyWithRetry(() => import('./steps/BirthYearStep'), 'birth_year');
const TargetDateStep = lazyWithRetry(() => import('./steps/TargetDateStep'), 'target_date');
const WeightProjectionStep = lazyWithRetry(() => import('./steps/WeightProjectionStep'), 'weight_projection');
const WalkingHistoryStep = lazyWithRetry(() => import('./steps/WalkingHistoryStep'), 'walking_history');
const WeightPatternStep = lazyWithRetry(() => import('./steps/WeightPatternStep'), 'weight_pattern');
const InterstitialReframe = lazyWithRetry(() => import('./steps/InterstitialReframe'), 'interstitial_reframe');
const PrimaryGoalStep = lazyWithRetry(() => import('./steps/PrimaryGoalStep'), 'primary_goal');
const MainBarrierStep = lazyWithRetry(() => import('./steps/MainBarrierStep'), 'main_barrier');
const PersonalizedBlockersStep = lazyWithRetry(() => import('./steps/PersonalizedBlockersStep'), 'personalized_blockers');
const ExitIntentOverlay = lazyWithRetry(() => import('./paywall/ExitIntentOverlay'), 'payment_exit_overlay');

// Late-funnel steps — lazy loaded (user won't reach these for minutes).
// lazyWithRetry recovers from stale-deploy chunk 404s instead of bricking the funnel.
const CommitmentCheckStep = lazyWithRetry(() => import('./steps/CommitmentCheckStep'), 'commitment_check');
const TestimonialReviewStep = lazyWithRetry(() => import('./steps/TestimonialReviewStep'), 'testimonial_review');
const InterstitialRewards = lazyWithRetry(() => import('./steps/InterstitialRewards'), 'interstitial_rewards');
const RewardPreferenceStep = lazyWithRetry(() => import('./steps/RewardPreferenceStep'), 'reward_preference');
const ReviewLoadingStep = lazyWithRetry(() => import('./steps/ReviewLoadingStep'), 'review_loading');
const EmailCaptureStep = lazyWithRetry(() => import('./steps/EmailCaptureStep'), 'email_capture');
const ReferralRewardsStep = lazyWithRetry(() => import('./steps/ReferralRewardsStep'), 'referral_rewards');
const JourneyStoryStep = lazyWithRetry(() => import('./steps/JourneyStoryStep'), 'journey_story');
const PlanLoadingStep = lazyWithRetry(() => import('./steps/PlanLoadingStep'), 'plan_loading');
const StartingPointStep = lazyWithRetry(() => import('./steps/StartingPointStep'), 'starting_point');
const PlanStagesStep = lazyWithRetry(() => import('./steps/PlanStagesStep'), 'plan_stages');
const ExpectedResultsStep = lazyWithRetry(() => import('./steps/ExpectedResultsStep'), 'expected_results');
const ResultsPage = lazyWithRetry(() => import('./steps/ResultsPage'), 'results');
const PaywallStep = lazyWithRetry(() => import('./steps/PaywallStep'), 'paywall');

// Post-paywall — lazy loaded (includes Stripe, account creation, etc.)
// Keep raw import functions so we can prefetch chunks early
const paymentStepImport = () => import('./steps/PaymentStep');
const createAccountStepImport = () => import('./steps/CreateAccountStep');
const coachingUpsellStepImport = () => import('./steps/CoachingUpsellStep');
const exclusiveOfferStepImport = () => import('./steps/ExclusiveOfferStep');

const PaymentStep = lazyWithRetry(paymentStepImport, 'payment');
const CreateAccountStep = lazyWithRetry(createAccountStepImport, 'create_account');
const CoachingUpsellStep = lazyWithRetry(coachingUpsellStepImport, 'coaching_upsell');
const ExclusiveOfferStep = lazyWithRetry(exclusiveOfferStepImport, 'exclusive_offer');
const GetAppStep = lazyWithRetry(() => import('./steps/GetAppStep'), 'get_app');

// Number of quiz input steps completed before review loading starts.
const QUIZ_INPUT_STEPS = getQuestionSteps('review_loading').length;

const POST_PAYMENT_STEPS = new Set<FunnelV3Step>(['create_account', 'coaching_upsell', 'exclusive_offer', 'get_app']);

// sessionStorage keys guarding once-per-session analytics events across remounts/refreshes
const QUIZ_COMPLETED_FIRED_KEY = 'mc_quiz_completed_fired';
const QUIZ_STARTED_AT_KEY = 'mc_quiz_started_at';

// Resolve deferred-payment Elements options for the ExpressCheckoutElement on PaymentStep.
function buildPaymentElementsOptions(storeData: {
    selectedPlan: PlanType | null;
    checkoutBaseAmountCents: number;
    exitDiscountActive: boolean;
    promoDiscountPercent: number;
    referralDiscountPercent: number;
    backFromPayment: boolean;
    email: string;
}): StripeElementsOptions | undefined {
    const chargeAmount = getExpressCheckoutAmountCents({
        selectedPlan: storeData.selectedPlan,
        checkoutBaseAmountCents: storeData.checkoutBaseAmountCents,
        exitDiscountActive: storeData.exitDiscountActive,
        promoDiscountPercent: storeData.promoDiscountPercent,
        referralDiscountPercent: storeData.referralDiscountPercent,
        backFromPayment: storeData.backFromPayment,
    });
    if (chargeAmount == null) return undefined;

    return {
        mode: 'payment',
        amount: chargeAmount,
        currency: 'usd',
        paymentMethodCreation: 'manual',
        ...(storeData.email && {
            defaultValues: {
                billingDetails: {
                    email: storeData.email,
                },
            },
        }),
        appearance: {
            theme: 'stripe',
            variables: {
                borderRadius: '8px',
            },
        },
    };
}

const FunnelV3Flow: React.FC = () => {
    const { currentStep, setStep, data, updateField } = useFunnelV3Store();
    const [storeHydrated, setStoreHydrated] = useState(
        () => useFunnelV3Store.persist.hasHydrated(),
    );
    const { isAuthenticated } = useAuth();
    const prevStepRef = useRef<FunnelV3Step>(currentStep);
    const [isAnimating, setIsAnimating] = useState(false);
    // Quiz start survives refreshes so quiz_completed reports real duration
    const [quizStartedAt] = useState(() => {
        const stored = Number(getSessionStorageItem(QUIZ_STARTED_AT_KEY) || '');
        if (Number.isFinite(stored) && stored > 0) return stored;
        const now = Date.now();
        setSessionStorageItem(QUIZ_STARTED_AT_KEY, String(now));
        return now;
    });
    const quizCompletedFiredRef = useRef(false);
    // Latest store data for analytics payloads without re-firing step effects
    const dataRef = useRef(data);
    dataRef.current = data;

    // Exit intent state for payment→paywall back-button intercept
    const [showPaymentExitOverlay, setShowPaymentExitOverlay] = useState(false);

    const handlePaymentBeforeBack = useCallback((): boolean => {
        if (!data.exitDiscountActive) {
            setShowPaymentExitOverlay(true);
            return true; // intercept navigation
        }
        return false; // discount already active, proceed normally
    }, [data.exitDiscountActive]);

    const handlePaymentExitClaim = useCallback(() => {
        updateField('exitDiscountActive', true);
        window.setTimeout(() => {
            setShowPaymentExitOverlay(false);
            updateField('backFromPayment', true);
            setStep('paywall');
        }, 280);
    }, [updateField, setStep]);

    const handlePaymentExitDismiss = useCallback(() => {
        setShowPaymentExitOverlay(false);
        updateField('backFromPayment', true);
        setStep('paywall');
    }, [updateField, setStep]);

    // Fire Meta PageView once on mount — required for pixel activation
    useEffect(() => { trackPageView(); }, []);

    useEffect(() => {
        if (useFunnelV3Store.persist.hasHydrated()) {
            setStoreHydrated(true);
            return;
        }
        return useFunnelV3Store.persist.onFinishHydration(() => setStoreHydrated(true));
    }, []);

    // Capture coupon code from URL early (before EmailCaptureStep) so deep-links work
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const promoCode = params.get('promo') || params.get('coupon') || getSessionStorageItem('couponCode');
        if (promoCode) {
            const code = promoCode.toUpperCase();
            setSessionStorageItem('couponCode', code);
            updateField('stripeCouponCode', code);
        }
    }, [updateField]);

    // Persist ?ref= / /join/:code into the quiz store and validate for the landing banner.
    useEffect(() => {
        const code = captureReferralCodeFromUrl();
        if (!code) {
            return;
        }
        updateField('promoCode', code);
        let cancelled = false;
        void (async () => {
            const result = await validateReferralCode(code);
            if (cancelled) {
                return;
            }
            if (result.isValid) {
                if (result.referrerFirstName) {
                    updateField('referrerFirstName', result.referrerFirstName);
                    trackReferralBannerShown({ referrerFirstName: result.referrerFirstName });
                }
                return;
            }
            trackReferralBannerCodeInvalid();
        })();
        return () => {
            cancelled = true;
        };
    }, [updateField]);

    // Deep-link: ?step=paywall lets an authenticated user skip straight to the paywall
    // (e.g. returning via a sign-in email link processed by TokenAuthProvider).
    // The token exchange is a network call of unknown duration, so this reacts to
    // isAuthenticated flipping true instead of racing it with a fixed timer.
    const paywallDeepLinkDoneRef = useRef(false);
    useEffect(() => {
        if (paywallDeepLinkDoneRef.current) return;
        const step = new URLSearchParams(window.location.search).get('step');
        if (step !== 'paywall') return;
        let hasToken = false;
        try {
            hasToken = !!localStorage.getItem('accessToken');
        } catch { /* storage disabled */ }
        if (!isAuthenticated && !hasToken) return;

        let cancelled = false;
        void (async () => {
            const sessionOk = await ensureFunnelAccessToken();
            if (cancelled || paywallDeepLinkDoneRef.current) return;
            if (!sessionOk && !isAuthenticated) return;
            paywallDeepLinkDoneRef.current = true;
            if (!sessionOk) {
                await hydrateQuizFromProfile();
                if (!cancelled) setStep('paywall');
                return;
            }
            const destination = await destinationAfterSession();
            if (!cancelled) setStep(destination);
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, setStep]);

    // Deep-link: ?step=email_capture drops an abandoned-cart lead on the email
    // screen. Deliberately ungated, unlike ?step=paywall — mail apps open links
    // in their own browser, so these visitors arrive with no session, and
    // submitting an address we already know signs them in and routes to the
    // paywall on its own. No token to mint, expire, or get burned by a scanner.
    const emailCaptureDeepLinkDoneRef = useRef(false);
    useEffect(() => {
        if (emailCaptureDeepLinkDoneRef.current) return;
        const step = new URLSearchParams(window.location.search).get('step');
        if (step !== 'email_capture') return;
        if (data.paymentCompleted) {
            emailCaptureDeepLinkDoneRef.current = true;
            return;
        }
        let hasToken = false;
        try {
            hasToken = !!localStorage.getItem('accessToken');
        } catch { /* storage disabled */ }
        if (!isAuthenticated && !hasToken) {
            emailCaptureDeepLinkDoneRef.current = true;
            setStep('email_capture');
            return;
        }
        let cancelled = false;
        void (async () => {
            const sessionOk = await ensureFunnelAccessToken();
            if (cancelled || emailCaptureDeepLinkDoneRef.current) return;
            emailCaptureDeepLinkDoneRef.current = true;
            if (!sessionOk) {
                setStep('email_capture');
                return;
            }
            const destination = await destinationAfterSession();
            if (!cancelled) setStep(destination);
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, data.paymentCompleted, setStep]);

    // Discount emails open /checkout. A new browser signs in from email capture
    // (promo already stored). A browser that already paid resumes account setup
    // instead of the quiz or a second charge.
    const checkoutResumeDoneRef = useRef(false);
    useEffect(() => {
        if (checkoutResumeDoneRef.current || !storeHydrated) return;
        const path = window.location.pathname.replace(/\/+$/, '') || '/';
        if (path !== '/checkout') return;
        checkoutResumeDoneRef.current = true;

        const snapshot = useFunnelV3Store.getState();
        if (snapshot.data.paymentCompleted) {
            if (!POST_PAYMENT_STEPS.has(snapshot.currentStep)) {
                setStep('create_account');
            }
            return;
        }

        let hasToken = false;
        try {
            hasToken = !!localStorage.getItem('accessToken');
        } catch { /* storage disabled */ }
        if (!isAuthenticated && !hasToken) {
            setStep('email_capture');
            return;
        }

        const authed = isAuthenticated;
        void (async () => {
            const sessionOk = await ensureFunnelAccessToken();
            if (!sessionOk && !authed) {
                setStep('email_capture');
                return;
            }
            if (!sessionOk) {
                setStep('paywall');
                return;
            }
            const destination = await destinationAfterSession();
            setStep(destination);
        })();
    }, [storeHydrated, isAuthenticated, setStep]);

    // Guard: redirect to payment if user lands on a post-payment step without having paid
    useEffect(() => {
        if (POST_PAYMENT_STEPS.has(currentStep) && !data.paymentCompleted) {
            setStep('payment');
        }
    }, [currentStep, data.paymentCompleted, setStep]);

    // Assign sticky default-plan A/B variant + pricing tier shadow on first paywall entry
    useEffect(() => {
        if (currentStep !== 'paywall') return;
        ensureAbDefaultPlanVariant();
        ensureDynamicPricingVariant();
        const { result, isNewAssignment } = ensurePricingTier(
            buildPricingTierSignalsFromFunnelData(data)
        );
        if (isNewAssignment) {
            trackPricingTierAssigned({
                pricing_tier: result.tier,
                pricing_band: getPricingBand(result.tier),
                cp_score: result.cpScore,
                vp_score: result.vpScore,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- assign once per paywall visit; tier is sticky
    }, [currentStep]);

    // Track every step view — fires exactly once per step CHANGE. Store data
    // mutations (plan selection, coupon writes, unit toggles) must not re-fire
    // it, so the quiz_completed payload reads through dataRef instead of
    // depending on `data`.
    useEffect(() => {
        const stepNumber = FUNNEL_V3_STEP_ORDER.indexOf(currentStep) + 1;
        trackStepViewed(currentStep, stepNumber);

        // Fire quiz_completed once when all quiz questions are answered.
        // sessionStorage guard survives remounts (refresh on review_loading).
        if (
            currentStep === 'review_loading' &&
            !quizCompletedFiredRef.current &&
            getSessionStorageItem(QUIZ_COMPLETED_FIRED_KEY) !== '1'
        ) {
            quizCompletedFiredRef.current = true;
            setSessionStorageItem(QUIZ_COMPLETED_FIRED_KEY, '1');
            const quizData = dataRef.current;
            trackQuizCompleted({
                totalQuestionsAnswered: QUIZ_INPUT_STEPS,
                totalTimeMs: Date.now() - quizStartedAt,
                primaryGoal: quizData.primaryGoal,
                painPoints: [
                    ...quizData.stressSymptoms,
                    ...(quizData.physicalBarriers || []),
                ],
                activityLevel: quizData.activityLevel,
                ageRange: quizData.ageGroup,
                walkingHistory: quizData.walkingHistory,
                weightPattern: quizData.weightPattern,
                currentWeightKg: quizData.currentWeightKg,
                goalWeightKg: quizData.goalWeightKg,
                heightCm: quizData.heightCm,
                birthYear: quizData.birthYear,
                stressSymptoms: quizData.stressSymptoms,
                sleepQuality: quizData.sleepQuality,
                physicalBarriers: quizData.physicalBarriers,
                targetZones: quizData.targetZones,
                mainBarrier: quizData.mainBarrier,
                motivation: quizData.motivation,
                rewardPreference: quizData.rewardPreference,
            });
        }
    }, [currentStep, quizStartedAt]);

    // Scroll to top + fade animation on every step change
    // useLayoutEffect prevents the 1-frame flash of new content before opacity-0
    useLayoutEffect(() => {
        if (prevStepRef.current !== currentStep) {
            window.scrollTo(0, 0);
            setIsAnimating(true);
            prevStepRef.current = currentStep;
        }
    }, [currentStep]);

    useEffect(() => {
        if (isAnimating) {
            const timer = setTimeout(() => setIsAnimating(false), 50);
            return () => clearTimeout(timer);
        }
    }, [isAnimating]);

    // Preload Stripe.js + payment chunk when user reaches paywall
    const prefetchedRef = useRef(false);
    useEffect(() => {
        if (currentStep === 'paywall' && !prefetchedRef.current) {
            prefetchedRef.current = true;
            preloadStripe();
            paymentStepImport();
        }
    }, [currentStep]);

    // Browser history — push entries so back button goes to previous step.
    // Paid users are held in place (never back into paywall/payment/quiz, which
    // risks confusion and duplicate-charge attempts). Back from payment gets the
    // same exit-intent intercept as the on-page back button.
    const handlePopState = useCallback(() => {
        const currIndex = FUNNEL_V3_STEP_ORDER.indexOf(currentStep);
        if (currIndex <= 0) return;

        if (data.paymentCompleted) {
            window.history.pushState({ step: currentStep }, '', '');
            return;
        }

        if (currentStep === 'payment') {
            if (!data.exitDiscountActive) {
                window.history.pushState({ step: currentStep }, '', '');
                setShowPaymentExitOverlay(true);
                return;
            }
            updateField('backFromPayment', true);
        }

        let targetIndex = currIndex - 1;

        // Skip loading screens and timed interstitials — they auto-advance and
        // would bounce the user forward again.
        while (targetIndex >= 0 && shouldSkipOnBack(FUNNEL_V3_STEP_ORDER[targetIndex])) {
            targetIndex--;
        }

        if (targetIndex >= 0) {
            setStep(FUNNEL_V3_STEP_ORDER[targetIndex]);
        }
    }, [currentStep, setStep, updateField, data.paymentCompleted, data.exitDiscountActive]);

    useEffect(() => {
        window.history.pushState({ step: currentStep }, '', '');
    }, [currentStep]);

    useEffect(() => {
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [handlePopState]);

    // Close the payment exit overlay if the step changes underneath it
    useEffect(() => {
        if (currentStep !== 'payment') {
            setShowPaymentExitOverlay(false);
        }
    }, [currentStep]);

    const renderStep = () => {
        switch (currentStep) {
            case 'motivation':
                return <MotivationStep />;
            case 'motivation_intro':
                return <MotivationInterstitial />;
            case 'goals_intro':
                return <GoalsIntroStep />;
            case 'age':
                return <AgeStep />;
            case 'activity':
                return <ActivityStep />;
            case 'interstitial_metabolism':
                return <InterstitialMetabolism />;
            case 'stress_symptoms':
                return <StressSymptomsStep />;
            case 'sleep':
                return <SleepStep />;
            case 'interstitial_sleep':
                return <InterstitialSleep />;
            case 'physical_barriers':
                return <PhysicalBarriersStep />;
            case 'target_zones':
                return <TargetZonesStep />;
            case 'interstitial_science':
                return <InterstitialScience />;
            case 'height':
                return <HeightStep />;
            case 'current_weight':
                return <CurrentWeightStep />;
            case 'goal_weight':
                return <GoalWeightStep />;
            case 'birth_year':
                return <BirthYearStep />;
            case 'target_date':
                return <TargetDateStep />;
            case 'weight_projection':
                return <WeightProjectionStep />;
            case 'walking_history':
                return <WalkingHistoryStep />;
            case 'weight_pattern':
                return <WeightPatternStep />;
            case 'interstitial_reframe':
                return <InterstitialReframe />;
            case 'primary_goal':
                return <PrimaryGoalStep />;
            case 'main_barrier':
                return <MainBarrierStep />;
            case 'personalized_blockers':
                return <PersonalizedBlockersStep />;
            case 'testimonial_review':
                return <TestimonialReviewStep />;
            case 'interstitial_rewards':
                return <InterstitialRewards />;
            case 'reward_preference':
                return <RewardPreferenceStep />;
            case 'review_loading':
                return <ReviewLoadingStep />;
            case 'email_capture':
                return <EmailCaptureStep />;
            case 'referral_rewards':
                return <ReferralRewardsStep />;
            case 'journey_story':
                return <JourneyStoryStep />;
            case 'plan_loading':
                return <PlanLoadingStep />;
            case 'starting_point':
                return <StartingPointStep />;
            case 'plan_stages':
                return <PlanStagesStep />;
            case 'commitment_check':
                return <CommitmentCheckStep />;
            case 'expected_results':
                return <ExpectedResultsStep />;
            case 'results':
                return <ResultsPage />;
            case 'paywall':
                return <PaywallStep />;
            case 'payment': {
                const paymentElementsOptions = buildPaymentElementsOptions(data);
                const walletAmountKey =
                    paymentElementsOptions && 'amount' in paymentElementsOptions
                        ? paymentElementsOptions.amount
                        : 'none';
                return (
                    <Sentry.ErrorBoundary fallback={PaymentErrorFallback}>
                        <StripeProvider key={walletAmountKey} options={paymentElementsOptions}>
                            <PaymentStep onBeforeBack={handlePaymentBeforeBack} />
                        </StripeProvider>
                    </Sentry.ErrorBoundary>
                );
            }
            case 'create_account':
                return <CreateAccountStep />;
            case 'coaching_upsell':
                return <ErrorBoundary><StripeProvider><CoachingUpsellStep /></StripeProvider></ErrorBoundary>;
            case 'exclusive_offer':
                return <ErrorBoundary><StripeProvider><ExclusiveOfferStep /></StripeProvider></ErrorBoundary>;
            case 'get_app':
                return <GetAppStep />;
            default:
                return null;
        }
    };

    // Long conversion / post-pay screens keep their own internal layout; wrap so
    // they scroll inside the locked dvh shell (quiz steps use FunnelV3Screen).
    const needsScrollWrap =
        currentStep === 'paywall' ||
        currentStep === 'payment' ||
        currentStep === 'create_account' ||
        currentStep === 'coaching_upsell' ||
        currentStep === 'exclusive_offer' ||
        currentStep === 'get_app' ||
        currentStep === 'results' ||
        currentStep === 'plan_loading' ||
        currentStep === 'review_loading' ||
        currentStep === 'weight_projection' ||
        currentStep === 'journey_story' ||
        currentStep === 'referral_rewards';

    const stepNode = renderStep();

    return (
        <div className="w-full max-w-[430px] mx-auto min-h-[100dvh] h-[100dvh] flex flex-col overflow-hidden bg-white">
            <FunnelV3ProgressBar currentStep={currentStep} setStep={setStep} />
            <div
                className={`flex-1 flex flex-col min-h-0 transition-opacity duration-200 ease-out ${
                    isAnimating ? 'opacity-0' : 'opacity-100'
                }`}
            >
                <Suspense fallback={
                    <div className="flex-1 bg-white flex items-center justify-center">
                        <div className="w-8 h-8 border-3 border-[#369fff] border-t-transparent rounded-full animate-spin" />
                    </div>
                }>
                    {needsScrollWrap ? (
                        <div className="flex-1 min-h-0 overflow-y-auto">{stepNode}</div>
                    ) : (
                        stepNode
                    )}
                </Suspense>
            </div>

            {/* Exit intent overlay — shown when user clicks back from payment */}
            {showPaymentExitOverlay && (
                <Suspense fallback={null}>
                    <ExitIntentOverlay
                        isVisible
                        quizData={{
                            primaryGoal: data.primaryGoal as 'confidence' | 'health_energy' | 'clothes_fit' | 'sleep_stress' | undefined,
                            mainBarrier: data.mainBarrier as 'lose_motivation' | 'no_results_fast' | 'too_busy' | 'uncertainty' | undefined,
                            firstName: data.firstName || undefined,
                            weight: data.currentWeightKg ?? undefined,
                            weightGoal: data.goalWeightKg ?? undefined,
                            weightUnit: data.weightUnit === 'lbs' ? 'lb' : 'kg',
                            targetDate: data.targetDate ?? undefined,
                        }}
                        selectedPlan={(data.selectedPlan ?? '4_week') as PlanType}
                        onClaim={handlePaymentExitClaim}
                        onDismiss={handlePaymentExitDismiss}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default FunnelV3Flow;
